import { Lock, LockOpen, Plus } from "@phosphor-icons/react"
import { createFileRoute } from "@tanstack/react-router"
import { addWeeks, differenceInWeeks, getWeek } from "date-fns"
import { flatten, min, max, minBy, maxBy, round, sum, sumBy } from "lodash"
import { useMemo, useState } from "react"
import TitledCard from "~/components/collections/cards/TitledCard"
import CollectionsSelect from "~/components/collections/CollectionsSelect"
import { AnalysisPage } from "~/components/design-system/pages"
import ViewConfiguration from "~/components/planning/ViewConfiguration"
import { Button } from "~/components/reusable/Button"
import StaticBarChart, {
  type StaticBarChartDataPoint,
} from "~/components/reusable/graphs/StaticBarChart"
import SuperChart, { type CategoryChartSeries } from "~/components/reusable/graphs/SuperChart"
import { Input } from "~/components/reusable/Input"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import Select from "~/components/reusable/Select"
import { Separator } from "~/components/reusable/Separator"
import { Switch } from "~/components/reusable/Switch"
import { Tooltip } from "~/components/reusable/Tooltip"
import { ChartType, LegendAlignment } from "~/constants/graphs"
import { useCollectionContext } from "~/context/CollectionProvider"
import { useDemandPlanContext, type VariantWithDemandMetrics } from "~/context/DemandPlanProvider"
import { useGetCollections, type ICollectionSimple } from "~/hooks/useCollections"
import {
  useGetDemandPlanSeasonalityProfile,
  useUpsertDemandPlanSeasonality,
} from "~/hooks/useDemandPlan"
import { useGetHistoricCollectionSeasonality } from "~/hooks/useDSAPi"
import { usePageTracking } from "~/hooks/usePageTracking"
import { startOfDayUTC } from "~/utils/datetime"
import { formatName, joinCollectionIds, splitCollectionIdString } from "~/utils/namingUtils"
import { getTailwindValue } from "~/utils/tailwind"
import { filterDefined } from "~/utils/tsUtils"

export const Route = createFileRoute(
  "/_auth/demand-planning/fashion/$collectionId/_layout/seasonality",
)({
  component: RouteComponent,
})

type SeasonalityMetrics = {
  seasonalityProfile: Array<number>
  collectionProfile: Array<number>
  mixedProfile: Array<number>
  seasonalitySales: Array<number>
  collectionSales: Array<number>
  mixedSales: Array<number>
}

type SeasonalityRow = {
  week: number
  key: string
  seasonality: number
  intro: number
  collection: number
}

const calculateSeasonalitySales = (
  variant: VariantWithDemandMetrics,
  startDate: Date | null,
  endDate: Date | null,
  variantSeasonalityProfiles: Array<SeasonalityRow>,
): SeasonalityMetrics => {
  if (startDate == null || endDate == null) {
    return {
      seasonalityProfile: [],
      collectionProfile: [],
      mixedProfile: [],
      seasonalitySales: [],
      collectionSales: [],
      mixedSales: [],
    }
  }
  const collectionWeekLength = differenceInWeeks(endDate, startDate) + 1
  let collectionProfile = variantSeasonalityProfiles
    .map((row) => row.collection)
    .slice(0, collectionWeekLength)
  let seasonalityProfile = variantSeasonalityProfiles.map((row) => row.seasonality)
  const startWeek = getWeek(startDate) - 1
  for (let i = 0; i < startWeek; i++) {
    const row = seasonalityProfile.shift()
    if (row) {
      seasonalityProfile.push(row)
    }
  }
  seasonalityProfile = seasonalityProfile.slice(0, collectionWeekLength)
  const seasonalitySum = sum(seasonalityProfile)
  seasonalityProfile = seasonalityProfile.map((n) => n / seasonalitySum)
  const collectionSum = sum(collectionProfile)
  collectionProfile = collectionProfile.map((n) => n / collectionSum)
  const mixedProfile = collectionProfile.map((n, i) => (n + (seasonalityProfile[i] ?? 0)) / 2)
  const seasonalitySales = seasonalityProfile.map((n) => n * variant.forecastTotal)
  const collectionSales = collectionProfile.map((n) => n * variant.forecastTotal)
  const mixedSales = mixedProfile.map((n) => n * variant.forecastTotal)
  return {
    seasonalityProfile,
    collectionProfile,
    mixedProfile,
    collectionSales,
    seasonalitySales,
    mixedSales,
  }
}

function RouteComponent() {
  usePageTracking("Collection - Seasonality")
  /**
   * Consts
   */
  const profileOptions = [
    { label: "Default", value: "collectionSales" },
    { label: "Historical", value: "seasonalitySales" },
    { label: "Blended", value: "mixedSales" },
  ]
  const defaultProfile = profileOptions[0]!
  const dataSets: SeasonalityChartDataset[] = []

  /**
   * Contexts
   */
  const {
    sourceCollectionIds,
    sourceCollectionName,
    sourceCollections,
    filteredVariants,
    demandPlanSeasonality,
    demandPlanId,
    variantsLoading,
  } = useDemandPlanContext()
  const { collections } = useCollectionContext()
  /**
   * State
   */
  // TODO: Consider making an array of objects instead of seperate arrays to ensure coupling of different values
  const [selectedProfileState, setSelectedProfileState] = useState<
    | {
        label: string
        value: string
      }
    | undefined
  >(undefined)
  const [customProfileState, setCustomProfileState] = useState<number[] | undefined>(undefined)
  const [customProfileInputsState, setCustomProfileInputsState] = useState<string[] | undefined>(
    undefined,
  )
  const [comparisonCollectionsState, setComparisonCollectionsState] = useState<
    Array<Array<ICollectionSimple> | null> | undefined
  >(undefined)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [lockedSeasonalityColumnsState, setLockedSeasonalityColumnsState] = useState<
    boolean[] | undefined
  >(undefined)

  /**
   * API
   */
  const { data: allCollections } = useGetCollections()
  const upsertDemandPlanSeasonality = useUpsertDemandPlanSeasonality()
  // TODO: This fetches all profiles (which should not be needed). This is 75Mb of data on Carhartt. We need to avoid this!!!
  const { data: seasonalityData } = useGetDemandPlanSeasonalityProfile(
    undefined,
    Boolean(demandPlanId),
  )
  const loadedCollections = demandPlanSeasonality?.comparisonCollections.map(
    (collectionsString) => {
      const collectionIds = splitCollectionIdString(collectionsString)
      return filterDefined(
        collectionIds.map((id) => allCollections?.find((col) => `${col.id}` === id)),
      )
    },
  )

  /**
   * Seasonality calculations
   */
  const [sourceStartDate, sourceEndDate] = useMemo(
    () => [
      minBy(sourceCollections, "startDate")?.startDate ?? null,
      maxBy(sourceCollections, "endDate")?.endDate ?? null,
    ],
    [sourceCollections],
  )

  const seasonalityByCategory = useMemo(() => {
    const map = new Map<string, Array<SeasonalityRow>>()
    for (const row of seasonalityData ?? []) {
      if (!map.has(row.key)) {
        map.set(row.key, [])
      }
      map.get(row.key)!.push(row)
    }
    return map
  }, [seasonalityData])

  /**
   * Variable extraction/remapping
   */

  const unsavedChanges = !!selectedProfileState || !!customProfileState
  // Add logic that uses all source collections
  const sourceCollection = collections[0]
  const collectionWeekLength =
    sourceCollection?.startDate && sourceCollection?.endDate
      ? differenceInWeeks(sourceCollection.endDate, sourceCollection.startDate) + 1
      : 0

  const comparisonCollections = comparisonCollectionsState ?? loadedCollections ?? []
  const comparisonCollectionIds = comparisonCollections
    .filter((col) => col != null)
    .map((colList) => colList.map((col) => col.id))
  const { data: comparisonSeasonalities, isFetching: comparisonFetching } =
    useGetHistoricCollectionSeasonality(comparisonCollectionIds)

  const selectedProfile =
    selectedProfileState ??
    profileOptions.find((option) => option.value === demandPlanSeasonality?.seasonalityTemplate) ??
    defaultProfile

  const activeProfile =
    customProfileState ??
    (demandPlanSeasonality?.customSeasonality && demandPlanSeasonality?.customSeasonality.length > 0
      ? demandPlanSeasonality?.customSeasonality
      : aggregateVariantsSeasonality(
          filteredVariants,
          selectedProfile.value,
          collectionWeekLength,
          seasonalityByCategory,
          sourceStartDate,
          sourceEndDate,
        ))

  const customProfileInputs =
    customProfileInputsState ?? mapProfileValuesToInputs(activeProfile) ?? []

  const lockedSeasonalityColumns =
    lockedSeasonalityColumnsState ?? customProfileInputs.map(() => false)

  const allSelectedCollectionIds = [
    ...sourceCollectionIds,
    ...flatten(comparisonCollectionIds),
  ].filter((id) => id !== undefined && id !== null)

  /**
   * Helper functions
   */
  const changeProfile = (profileName?: string) => {
    const profile = profileOptions.find((option) => option.value === profileName) ?? defaultProfile
    const sourceSeasonality = aggregateVariantsSeasonality(
      filteredVariants,
      profile?.value,
      collectionWeekLength,
      seasonalityByCategory,
      sourceStartDate,
      sourceEndDate,
    )
    setSelectedProfileState(profile)
    setCustomProfileState(sourceSeasonality)
    setCustomProfileInputsState(mapProfileValuesToInputs(sourceSeasonality))
  }

  const updateValue = (value: string, index: number) => {
    const validationRegex = /^\d*.?\d*%?$/g
    if (!validationRegex.test(value)) {
      return // Don't update for invalid values
    }
    const customProfileInputsCopy = [...customProfileInputs]
    customProfileInputsCopy[index] = `${value}`
    setCustomProfileInputsState(customProfileInputsCopy)
  }

  const updateCustomProfile = () => {
    const newProfileValues = customProfileInputs.map((string) => {
      const parsedValue = parseFloat(string)
      if (isNaN(parsedValue)) {
        return 0
      }
      return max([min([parsedValue, 100])!, 0])! / 100
    })
    const balancedProfileValues = rebalanceProfileValues(
      activeProfile,
      newProfileValues,
      lockedSeasonalityColumns,
    )
    setCustomProfileState(balancedProfileValues)
    setCustomProfileInputsState(mapProfileValuesToInputs(balancedProfileValues))
  }

  const toggleLock = (index: number) => {
    const lockedColumnsCopy = [...lockedSeasonalityColumns]
    lockedColumnsCopy[index] = !lockedColumnsCopy[index]
    setLockedSeasonalityColumnsState(lockedColumnsCopy)
  }

  const saveChanges = () => {
    if (demandPlanId && selectedProfile) {
      upsertDemandPlanSeasonality.mutate({
        demandPlanId,
        seasonalityTemplate: selectedProfile.value,
        customSeasonality: activeProfile,
      })
      setEditMode(false)
    }
  }

  const updateComparisonCollections = (collections: Array<Array<ICollectionSimple> | null>) => {
    if (demandPlanId && selectedProfile) {
      upsertDemandPlanSeasonality.mutate({
        demandPlanId,
        seasonalityTemplate: demandPlanSeasonality?.seasonalityTemplate ?? defaultProfile.value,
        comparisonCollections: collections
          .filter((col) => col !== null)
          .map((colArr) => joinCollectionIds(colArr?.map((col) => `${col.id}`) ?? [])),
      })
      setComparisonCollectionsState(undefined)
    }
  }

  const discardChanges = () => {
    setSelectedProfileState(undefined)
    setCustomProfileState(undefined)
    setCustomProfileInputsState(undefined)
    setLockedSeasonalityColumnsState(undefined)
    setEditMode(false)
  }
  /**
   * Render
   */

  if (sourceCollection == undefined || sourceCollectionName == null || variantsLoading) {
    return <LoadingOverlay />
  }

  const seasonalityProfile = activeProfile.map((value, index) => {
    const date = addWeeks(startOfDayUTC(sourceCollection.startDate!), index)
    const locked = lockedSeasonalityColumns[index]
    return {
      value: value,
      name: `${date.getUTCDate()} / ${date.getUTCMonth() + 1}`,
      color: locked
        ? getTailwindValue(`--color-indigo-100`)
        : getTailwindValue(`--color-indigo-500`),
    }
  })
  const sourceSeasonalityData: SeasonalityChartDataset = {
    name: sourceCollectionName,
    color: "#6366F1",
    type: "column",
    data: seasonalityProfile,
  }
  dataSets.push(sourceSeasonalityData)

  comparisonSeasonalities?.forEach((seasonality, index) => {
    // TODO: consider if all comparison collections shouldn't be accounted for here
    const comparisonCollection = comparisonCollections[index]?.[0]
    const comparisonStartDate = comparisonCollection?.startDate
    const comparisonCollectionName =
      comparisonCollections[index]?.length && comparisonCollections[index]?.length > 1
        ? comparisonCollection?.groupName
        : comparisonCollection?.name
    if (comparisonStartDate != null && comparisonCollectionName != null) {
      const baselineSeasonality =
        seasonality?.data?.map((row) => {
          {
            const date = addWeeks(startOfDayUTC(comparisonStartDate), row.week - 1)
            return {
              name: `${date.getUTCDate()} / ${date.getUTCMonth() + 1}`, // TODO: Format date
              value: row.weight,
            }
          }
        }) ?? []
      const weekDiff = differenceInWeeks(sourceCollection.startDate!, comparisonStartDate)
      for (let i = 0; i < weekDiff; i++) {
        const item = baselineSeasonality.shift()
        if (item != undefined) {
          baselineSeasonality.push(item)
        }
      }
      const slicedBaselineSeasonality = baselineSeasonality.slice(0, collectionWeekLength)
      const baselineSeasonalityData: SeasonalityChartDataset = {
        name: comparisonCollectionName,
        color: getTailwindValue(`--color-orange-${300 + index * 200}`),
        type: "spline",
        data: slicedBaselineSeasonality,
      }
      dataSets.push(baselineSeasonalityData)
    }
  })
  const valueTotal = (dataSets[0] && sumBy(dataSets[0].data, (r) => r.value)) ?? 1

  return (
    <AnalysisPage
      fillContent
      contentClassName="gap-y-6"
      viewConfiguration={<ViewConfiguration showFilters showChannelSelect />}
    >
      <div className="w-full h-full flex flex-row justify-between">
        <div className="flex flex-row gap-x-2 h-7 w-full content-center label-sm align-middle">
          {comparisonCollections.map((cols, index) => (
            <CollectionsSelect
              key={index}
              selectedCollectionIds={cols?.map((col) => col.id) ?? []}
              onCollectionSelectChange={(collections) => {
                if (collections.length > 0) {
                  const comparisonCollectionsCopy = [...comparisonCollections]
                  comparisonCollectionsCopy[index] = collections
                  updateComparisonCollections(comparisonCollectionsCopy)
                  setComparisonCollectionsState(comparisonCollectionsCopy)
                } else {
                  updateComparisonCollections(comparisonCollections.filter((_, i) => i !== index))
                  setComparisonCollectionsState(comparisonCollections.filter((_, i) => i !== index))
                }
              }}
              className={"min-w-32"}
              excludedCollectionIds={allSelectedCollectionIds}
              autoFocus={cols === null}
            />
          ))}
          {comparisonCollections.length < 5 && (
            <Button
              size="sm"
              onClick={() => setComparisonCollectionsState([...comparisonCollections, null])}
              disabled={comparisonCollections.includes(null)}
              className={`disabled:border-dashed! disabled:border disabled:shadow-none disabled:bg-neutral-50!`}
            >
              <Plus className={comparisonCollections.includes(null) ? "fill-fg-muted!" : ""} />
              Compare
            </Button>
          )}
        </div>
        <div className="flex flex-row gap-x-2 h-7 w-full items-center label-sm align-middle justify-end">
          {editMode && (
            <>
              <Button
                disabled={!unsavedChanges}
                variant="neutral"
                className="self-end"
                onClick={discardChanges}
                size="sm"
              >
                Discard Changes
              </Button>
              <Button
                requires="edit"
                disabled={!unsavedChanges}
                variant="primary"
                className="self-end"
                onClick={saveChanges}
                size="sm"
              >
                Save Changes
              </Button>
              <Separator orientation="vertical" className="h-5 self-center" />
            </>
          )}
          Edit mode
          {editMode && unsavedChanges ? (
            <Tooltip body={"Save or discard changes to exit edit mode"}>
              <Switch disabled checked={editMode} onCheckedChange={setEditMode} />
            </Tooltip>
          ) : (
            <Switch checked={editMode} onCheckedChange={setEditMode} />
          )}
        </div>
      </div>
      <div className="flex flex-row gap-x-6 h-fit">
        <div className="flex flex-col h-full gap-y-6 grow shrink w-1/2 overflow-y-auto pb-4 p-1">
          <TitledCard
            className={"h-fit"}
            title={"Seasonality Profile"}
            content={<SeasonalityChart loading={comparisonFetching} dataSets={dataSets} />}
          />
          {editMode ? (
            <TitledCard
              className={"h-fit"}
              title={
                <div className="w-full h-full flex justify-evenly py-3">
                  <div className="w-full px-4">Week starting</div>
                  <div className="w-full px-4">% of total {formatName("sale", true)}</div>
                  {comparisonCollections[0] ? (
                    <div className="w-full px-4">
                      Δ vs{" "}
                      {comparisonCollections[0].length > 1
                        ? comparisonCollections[0][0]?.groupName
                        : comparisonCollections[0][0]?.name}
                    </div>
                  ) : (
                    <div className="w-full px-4">Add collection for comparison</div>
                  )}
                  <div className="w-full px-4">Status</div>
                </div>
              }
              content={
                <>
                  {dataSets[0] &&
                    dataSets[0].data.map((row, index) => {
                      const isLocked = lockedSeasonalityColumns[index] === true
                      return (
                        <div
                          key={row.name}
                          className={index > 0 ? "border-t border-solid border-base" : ""}
                        >
                          <div className="w-full h-full flex justify-evenly py-3">
                            <div className="w-full px-4">{row.name}</div>
                            <div className="w-full px-4">
                              <Input
                                className={isLocked ? `text-fg-muted!` : `text-fg-base!`}
                                type="text"
                                value={customProfileInputs[index]}
                                onInput={(event) => {
                                  updateValue(event.currentTarget.value, index)
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    updateCustomProfile()
                                  }
                                }}
                                onBlur={updateCustomProfile}
                                disabled={isLocked}
                                onFocus={(event) => event.target.select()}
                              />
                            </div>
                            <div className="w-full px-4">
                              {dataSets[1]?.data[index] == undefined
                                ? "None"
                                : `${round((row.value - dataSets[1].data[index].value) * 100, 1) > 0 ? "+" : ""}${round((row.value - dataSets[1].data[index].value) * 100, 1)} %`}
                            </div>
                            <div className="w-full px-4">
                              <Button
                                className={`${isLocked ? "text-fg-base" : "text-fg-muted"}`}
                                variant="transparent"
                                onClick={() => toggleLock(index)}
                                tabIndex={-1}
                              >
                                {isLocked ? (
                                  <>
                                    <Lock /> Locked
                                  </>
                                ) : (
                                  <>
                                    <LockOpen /> Unlocked
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                </>
              }
            />
          ) : (
            <TitledCard
              className={"h-fit"}
              title={
                <div className="w-full h-full flex justify-evenly py-3">
                  <div className="w-full px-4">Week starting</div>
                  <div className="w-full px-4">% of total {formatName("sale", true)}</div>
                  {comparisonCollections[0] ? (
                    <div className="w-full px-4">
                      Δ vs{" "}
                      {comparisonCollections[0].length > 1
                        ? comparisonCollections[0][0]?.groupName
                        : comparisonCollections[0][0]?.name}
                    </div>
                  ) : (
                    <div className="w-full px-4">Add collection for comparison</div>
                  )}
                </div>
              }
              content={
                <>
                  {dataSets[0] &&
                    dataSets[0].data.map((row, index) => (
                      <div
                        key={row.name}
                        className={index > 0 ? "border-t border-solid border-base" : ""}
                      >
                        <div className="w-full h-full flex justify-evenly py-3">
                          <div className="w-full px-4">{row.name}</div>
                          <div className="w-full px-4">
                            {round((row.value / valueTotal) * 100, 1)}%
                          </div>
                          <div className="w-full px-4">
                            {dataSets[1]?.data[index] == undefined
                              ? "None"
                              : `${round((row.value - dataSets[1].data[index].value) * 100, 1) > 0 ? "+" : ""}${round((row.value - dataSets[1].data[index].value) * 100, 1)} %`}
                          </div>
                        </div>
                      </div>
                    ))}
                </>
              }
            />
          )}
        </div>
        {editMode && (
          <div className={`flex pt-1 flex-col h-full min-w-85`}>
            <TitledCard
              className="w-full h-fit overflow-visible"
              title={
                <div className="py-3 px-4 gap-y-1 flex flex-col">
                  <div className="text-fg-base">Apply from comparison</div>
                  <div className="paragraph-sm text-fg-subtle">
                    Use an existing profile as a template.
                  </div>
                </div>
              }
              content={
                <div className="flex flex-col gap-y-2 w-full px-4 py-5">
                  <div className="label-base">Seasonality profile</div>
                  <Select
                    fullWidthMenu
                    size="sm"
                    options={profileOptions}
                    value={selectedProfile}
                    onChange={(option) => changeProfile(option?.value)}
                  />
                </div>
              }
            />
          </div>
        )}
      </div>
    </AnalysisPage>
  )
}

interface SeasonalityChartDataset {
  name: string
  color: string
  type: "column" | "spline"
  data: Array<StaticBarChartDataPoint>
}

interface SeasonalityChartProps {
  dataSets: Array<SeasonalityChartDataset>
  loading: boolean
}

const SeasonalityChart = ({ dataSets, loading }: SeasonalityChartProps) => {
  const chartData: Array<CategoryChartSeries> = dataSets.map((data) => ({
    colors: [data.color],
    name: data.name,
    type: data.type,
    data: data.data.map<{ category: string; value: number; color?: string }>((val) => {
      const { name, value, color } = val
      return {
        category: name,
        value: value,
        color: color ?? data.color,
      }
    }),
  }))

  if (loading) {
    return (
      <div className={"h-[300px] w-full"}>
        <LoadingOverlay />
      </div>
    )
  }
  return (
    <SuperChart
      height={300}
      type={ChartType.CategorySeries}
      data={chartData}
      showTooltip={false}
      compact={false}
      showYAxisLabels={false}
      showXAxisLabels={true}
      showTickLines={false}
      xAxisYOffset={16}
      disableAnimation={true}
      legendAlignment={LegendAlignment.Bottom}
      centerLegend
    />
  )
}

const mapProfileValuesToInputs = (values: number[]): string[] => {
  return values.map((value) => `${round(value * 100, 1)}%`)
}

const aggregateVariantsSeasonality = (
  variants: Array<VariantWithDemandMetrics>,
  profile: string,
  collectionWeekLength: number,
  seasonalityByCategory: Map<string, Array<SeasonalityRow>>,
  sourceStartDate: Date | null,
  sourceEndDate: Date | null,
): number[] => {
  return variants
    .map((variant) => {
      const seasonality = calculateSeasonalitySales(
        variant,
        sourceStartDate,
        sourceEndDate,
        seasonalityByCategory.get(variant.category ?? "") ?? [],
      )
      switch (profile) {
        case "seasonalitySales":
          return seasonality.seasonalitySales
        case "collectionSales":
          return seasonality.collectionSales
        case "mixedSales":
          return seasonality.mixedSales
        default:
          return seasonality.collectionSales
      }
    })
    .reduce((prev, curr) => {
      for (let i = 0; i < collectionWeekLength; i++) {
        prev[i] = (prev[i] ?? 0) + (curr[i] ?? 0)
      }
      return prev
    }, [])
    .map((value, _, values) => value / sum(values))
}

const rebalanceProfileValues = (
  oldValues: number[],
  newValues: number[],
  lockedColumns: boolean[],
): number[] => {
  newValues = filterDefined(newValues.map((value) => round(max([value, 0])!, 3))) // No input values can be less than 0
  oldValues = oldValues.map((value) => round(value, 3))
  let columnDiffs = newValues.map((newValue, index) => newValue - (oldValues[index] ?? newValue))
  const changedColumnIndexes = filterDefined(
    columnDiffs.map((_, i) => (columnDiffs[i] === 0 ? undefined : i)),
  )
  let totalDiff = sum(columnDiffs)
  // The sum of the changed values cannot exceed the sum of the unlocked columns
  // Find the sum of all the unlocked columns that are not part of the value updates, and sum them
  const unlockedColumns = oldValues.filter(
    (_, index) => lockedColumns[index] === false && columnDiffs[index] === 0,
  )
  const lockedColumnsSum = sum(oldValues.filter((_, i) => lockedColumns[i] === true))
  const valueRemainder = 1 - lockedColumnsSum
  const unlockedColumnsSum = sum(unlockedColumns)
  const changedColumnsSum = sum(newValues.filter((_, i) => columnDiffs[i] !== 0))
  // If the sum of diffs exceed the sum of available values, downscale them so that they match
  const downscaleModifier =
    changedColumnsSum <= valueRemainder ? 1 : valueRemainder / changedColumnsSum
  newValues = newValues.map((v, i) => {
    return columnDiffs[i] === 0 ? v : round(v * downscaleModifier, 3)
  })
  columnDiffs = newValues.map((newValue, index) => newValue - (oldValues[index] ?? newValue))
  totalDiff = sum(columnDiffs)

  // Finally, balance the totalDiff evenly across the unlocked columns that are not part of the value changes
  columnDiffs = columnDiffs.map((value, index) => {
    if (lockedColumns[index]) {
      return 0 // Never update locked columns
    }
    if (value !== 0) {
      return value // If value is not 0, this column is an updated column, and shouldn't be balanced
    }
    const weight = unlockedColumnsSum === 0 ? 0 : (oldValues[index] ?? 0) / unlockedColumnsSum
    return round(-(weight * totalDiff), 3)
  })

  const balancedValues = oldValues.map((value, index) =>
    round(value + (columnDiffs[index] ?? 0), 3),
  )

  let totalSum = round(sum(balancedValues), 3)
  let index = 0
  while (totalSum > 1) {
    const value = balancedValues[index]
    if (lockedColumns[index] === true || changedColumnIndexes.includes(index) || value! <= 0) {
      index = (index + 1) % oldValues.length
      continue
    }
    balancedValues[index] = value! - 0.001
    index = (index + 1) % oldValues.length
    totalSum = sum(balancedValues)
  }
  index = 0
  while (totalSum < 1) {
    const value = balancedValues[index]
    if (lockedColumns[index] === true || changedColumnIndexes.includes(index)) {
      index = (index + 1) % oldValues.length
      continue
    }
    balancedValues[index] = value! + 0.001
    index = (index + 1) % oldValues.length
    totalSum = sum(balancedValues)
  }

  return balancedValues
}

export default StaticBarChart
